'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetMarketChart, useGetMarketRecap } from '@morfi/backend-sdk-v2/client';
import { priceChartTargetBars } from '@morfi/chart-policy';
import { useTranslations } from 'next-intl';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { ChartControls } from '@/components/chart/chart-controls';
import { ChartSettingsMenu } from '@/components/chart/chart-settings-menu';
import { MarketChart, type MarketChartOutcome, type MarketChartTrade, type MarketChartTradeTapeEvent } from '@/components/chart/market-chart';
import { averageEntryPriceD9 } from '@/components/market/position-realisable';
import { AnimatedButton } from '@/components/ui/animated-button';
import { TooltipProvider } from '@/components/ui/app-tooltip';
import { CHART_CANVAS_HEIGHT_DESKTOP, CHART_CANVAS_HEIGHT_MOBILE } from '@/config/chart';
import { D6_SCALE, D9_SCALE } from '@/config/solana';
import { useMarketPositions, useOwnedMarketPositionScope } from '@/hooks/positions/use-market-positions';
import { candleChartTargetBars } from '@/lib/chart/candle-chart-density';
import { LIVE_WINDOW_SECONDS, resolveAvailableInterval } from '@/lib/chart/chart-factory';
import { isoToUnix } from '@/lib/chart/iso-time';
import { mapMarketChartRows, type SparseChartEntry } from '@/lib/chart/map-market-chart-rows';
import { breakEvenMarketCapD6, pickChartLeafConverter } from '@/lib/chart/sdk-mapping';
import type { TimeframeRenderedFrame } from '@/lib/chart/timeframe-rendered-frame';
import type { ChartInterval, YAxisProperty } from '@/lib/chart/types';
import { logger } from '@/lib/telemetry/logger';
import { useWinnerChartIntentAnchor } from '@/stores/chart-intent';
import { useChartSettingsStore } from '@/stores/chart-settings';
import { useWinnerLiveTrades } from './use-winner-live-trades';
import { useWinnerTimeframeFrame } from './use-winner-timeframe-frame';
import { useWinnerTradeMarkers } from './use-winner-trade-markers';
import { useWinnerTradeTape } from './use-winner-trade-tape';
import { WINNER_WINDOW_SECONDS, type WinnerChartRequest, winnerAvailableIntervals, winnerChartRequest } from './winner-chart-request';
import { buildWinnerChartSnapshot, type HistoricalWinnerChartInterval, type WinnerChartSnapshot, type WinnerChartSource } from './winner-chart-source';
const TIMEFRAME_TARGET_LOADING_SERIES_OPACITY = 0.5;
interface WinnerOutcomeChartProps {
  readonly marketPubkey: string;
  readonly outcomeMint: string;
  readonly originalOutcomeIndex: number;
  readonly tradingStartedAt: string;
  readonly tradesFrom: string;
  readonly label: string;
  readonly priceD9: string;
  readonly effectiveMarketCapD6: string;
  readonly indexedOutcomeMintSupplyD6: string;
}
interface WinnerChartPresentationData extends WinnerChartSnapshot {
  readonly sourceKind: WinnerChartSource['kind'];
}
interface WinnerOutcomePresentation {
  readonly outcome: MarketChartOutcome;
  readonly liveValues: Readonly<Record<number, number>> | undefined;
  readonly breakevens: ReturnType<typeof winnerBreakEven>;
}
function buildWinnerOutcomePresentation(args: {
  readonly props: Omit<WinnerOutcomeChartProps, 'tradingStartedAt'>;
  readonly yAxisProperty: YAxisProperty;
  readonly positions: ReturnType<typeof useMarketPositions>['rows'];
  readonly activeWalletPubkey: string | null;
}): WinnerOutcomePresentation {
  const {
    props
  } = args;
  const currentValue = args.yAxisProperty === 'price' ? fixedPointNumber(props.priceD9, D9_SCALE) : fixedPointNumber(props.effectiveMarketCapD6, D6_SCALE);
  return {
    outcome: {
      index: 0,
      title: props.label,
      twar: 0,
      price: fixedPointNumber(props.priceD9, D9_SCALE) ?? 0,
      marketCap: fixedPointNumber(props.effectiveMarketCapD6, D6_SCALE) ?? 0
    },
    liveValues: currentValue === undefined ? undefined : {
      0: currentValue
    },
    breakevens: winnerBreakEven({
      positions: args.positions,
      activeWalletPubkey: args.activeWalletPubkey,
      outcomeMint: props.outcomeMint,
      originalOutcomeIndex: props.originalOutcomeIndex,
      indexedOutcomeMintSupplyD6: props.indexedOutcomeMintSupplyD6
    })
  };
}
function useLiveCandleClock(enabled, anchorMinuteMs) {
  const $ = _c(3);
  const [nowSec, setNowSec] = useState(_temp);
  let t0;
  let t1;
  if ($[0] !== enabled) {
    t0 = () => {
      if (!enabled) {
        return;
      }
      const intervalId = setInterval(() => setNowSec(Math.floor(Date.now() / 1000)), 1000);
      return () => clearInterval(intervalId);
    };
    t1 = [enabled];
    $[0] = enabled;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return enabled ? nowSec : Math.floor(anchorMinuteMs / 1000);
}
function _temp() {
  return Math.floor(Date.now() / 1000);
}
function winnerTradeHistoryCutoffMs(request: WinnerChartRequest, anchorMinuteMs: number): number | undefined {
  if (anchorMinuteMs <= 0) return undefined;
  if (request.kind === 'live') return anchorMinuteMs - LIVE_WINDOW_SECONDS * 1_000;
  return undefined;
}
function winnerChartSource(args: {
  readonly request: WinnerChartRequest;
  readonly liveTradeRows: ReturnType<typeof useWinnerLiveTrades>;
  readonly liveCandleEnabled: boolean;
  readonly liveCandleNowSec: number;
  readonly chartData: SparseChartEntry | undefined;
  readonly rangeStart?: string;
}): WinnerChartSource {
  if (args.request.kind === 'live') {
    return {
      kind: 'live',
      trades: args.liveTradeRows,
      nowSec: args.liveCandleNowSec,
      candleMode: args.liveCandleEnabled
    };
  }
  const {
    bucketSeconds,
    ...data
  } = args.chartData ?? {
    line: [],
    ohlcv: []
  };
  return {
    kind: 'historical',
    interval: args.request.interval,
    data,
    candleWidthSeconds: bucketSeconds ?? 60,
    ...(args.rangeStart === undefined ? {} : {
      rangeStart: args.rangeStart
    })
  };
}
function buildWinnerTargetFrame(args: {
  readonly source: WinnerChartSource;
  readonly anchorMinuteMs: number;
  readonly originalOutcomeIndex: number;
  readonly yAxisProperty: YAxisProperty;
  readonly indexedOutcomeMintSupplyD6: string;
  readonly outcomeMint: string;
  readonly renderMode: 'line' | 'candle';
}): TimeframeRenderedFrame<WinnerChartPresentationData> {
  const snapshot = buildWinnerChartSnapshot(args.source, {
    originalOutcomeIndex: args.originalOutcomeIndex,
    yAxisProperty: args.yAxisProperty,
    indexedOutcomeMintSupplyD6: args.indexedOutcomeMintSupplyD6
  });
  const historyStartTime = args.source.kind === 'historical' && args.source.interval === 'all' ? rangeStartSeconds(args.source.rangeStart) ?? snapshot.chartData[0]?.line[0]?.time : undefined;
  const windowSecs = args.source.kind === 'live' ? LIVE_WINDOW_SECONDS : chartWindowSeconds(args.source.interval, historyStartTime, args.anchorMinuteMs);
  return {
    seriesKey: `${args.outcomeMint}|${args.yAxisProperty}|${args.renderMode}|${args.indexedOutcomeMintSupplyD6}`,
    data: {
      ...snapshot,
      sourceKind: args.source.kind
    },
    windowSecs,
    ...(args.renderMode === 'line' && historyStartTime !== undefined ? {
      historyStartTime
    } : {}),
    lineCurve: 'linear'
  };
}
function useWinnerChartFailureLogs(args) {
  const $ = _c(20);
  let t0;
  let t1;
  if ($[0] !== args.candleContractFingerprint || $[1] !== args.liveChartEnabled || $[2] !== args.marketPubkey) {
    t0 = () => {
      if (args.liveChartEnabled || args.candleContractFingerprint === undefined) {
        return;
      }
      logger.warn("winner_detail.chart_candle_contract_invalid", {
        marketPubkey: args.marketPubkey,
        fingerprint: args.candleContractFingerprint
      });
    };
    t1 = [args.liveChartEnabled, args.marketPubkey, args.candleContractFingerprint];
    $[0] = args.candleContractFingerprint;
    $[1] = args.liveChartEnabled;
    $[2] = args.marketPubkey;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  const backendTimeframe = args.request.kind === "historical" ? args.request.queryArgs.interval ?? "all" : "all";
  const requestStart = args.request.kind === "historical" ? args.request.queryArgs.from : undefined;
  const requestEnd = args.request.kind === "historical" ? args.request.queryArgs.to : undefined;
  let t2;
  let t3;
  if ($[5] !== args.chart.error || $[6] !== args.chart.isError || $[7] !== args.historyStage || $[8] !== args.interval || $[9] !== args.liveChartEnabled || $[10] !== args.outcomeMint || $[11] !== backendTimeframe || $[12] !== requestEnd || $[13] !== requestStart) {
    t2 = () => {
      if (args.liveChartEnabled || !args.chart.isError) {
        return;
      }
      logger.error("winner_detail.chart_fetch_failed", {
        outcomeMint: args.outcomeMint,
        historyStage: args.historyStage,
        errorClass: safeErrorClass(args.chart.error),
        selectedInterval: args.interval,
        backendTimeframe,
        requestStart: requestStart ?? null,
        requestEnd: requestEnd ?? null
      });
    };
    t3 = [args.chart.error, args.chart.isError, args.interval, args.liveChartEnabled, args.outcomeMint, args.historyStage, backendTimeframe, requestEnd, requestStart];
    $[5] = args.chart.error;
    $[6] = args.chart.isError;
    $[7] = args.historyStage;
    $[8] = args.interval;
    $[9] = args.liveChartEnabled;
    $[10] = args.outcomeMint;
    $[11] = backendTimeframe;
    $[12] = requestEnd;
    $[13] = requestStart;
    $[14] = t2;
    $[15] = t3;
  } else {
    t2 = $[14];
    t3 = $[15];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[16] !== args.marketPubkey || $[17] !== args.positionsError) {
    t4 = () => {
      if (!args.positionsError) {
        return;
      }
      logger.warn("winner_detail.break_even_position_fetch_failed", {
        marketPubkey: args.marketPubkey
      });
    };
    t5 = [args.marketPubkey, args.positionsError];
    $[16] = args.marketPubkey;
    $[17] = args.positionsError;
    $[18] = t4;
    $[19] = t5;
  } else {
    t4 = $[18];
    t5 = $[19];
  }
  useEffect(t4, t5);
}
export function WinnerOutcomeChart(t0) {
  const $ = _c(61);
  const {
    marketPubkey,
    outcomeMint,
    originalOutcomeIndex,
    tradingStartedAt,
    tradesFrom,
    label,
    priceD9,
    effectiveMarketCapD6,
    indexedOutcomeMintSupplyD6
  } = t0;
  const tError = useTranslations("errorBoundary");
  const tResolved = useTranslations("market.resolvedPanel");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["resolvedAt"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== marketPubkey) {
    t2 = {
      pubkey: marketPubkey,
      fields: t1
    };
    $[1] = marketPubkey;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      fetchOnce: true
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const recapQuery = useGetMarketRecap(t2, t3);
  let t4;
  let t5;
  if ($[4] !== marketPubkey || $[5] !== recapQuery.isError) {
    t4 = () => {
      if (recapQuery.isError) {
        logger.warn("winner_detail.chart_resolution_fetch_failed", {
          marketPubkey
        });
      }
    };
    t5 = [marketPubkey, recapQuery.isError];
    $[4] = marketPubkey;
    $[5] = recapQuery.isError;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  const resolvedAt = recapQuery.data?.resolvedAt;
  let t6;
  if ($[8] !== resolvedAt) {
    t6 = isoToUnix(resolvedAt);
    $[8] = resolvedAt;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const resolvedAtTimestamp = t6;
  let t7;
  if ($[10] !== resolvedAtTimestamp || $[11] !== tResolved) {
    t7 = resolvedAtTimestamp === undefined ? undefined : tResolved("statResolved");
    $[10] = resolvedAtTimestamp;
    $[11] = tResolved;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const resolvedAtLabel = t7;
  const anchorMinuteMs = useRollingMinuteAnchorMs();
  const intentAnchor = useWinnerChartIntentAnchor(outcomeMint);
  let t8;
  if ($[13] !== intentAnchor) {
    t8 = () => intentAnchor ?? roundDownToMinute(Date.now());
    $[13] = intentAnchor;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const [historyAnchorMinuteMs, setHistoryAnchorMinuteMs] = useState(t8);
  const availableIntervals = winnerAvailableIntervals(tradesFrom, anchorMinuteMs);
  const selectedInterval = useChartSettingsStore(_temp2);
  const setChartInterval = useChartSettingsStore(_temp3);
  const [intervalOverride, setIntervalOverride] = useState(null);
  const [plotWidth, setPlotWidth] = useState(null);
  const interval = resolveAvailableInterval(intervalOverride ?? selectedInterval, availableIntervals);
  let t10;
  let t9;
  if ($[15] !== intervalOverride || $[16] !== selectedInterval || $[17] !== setChartInterval) {
    t9 = () => {
      if (intervalOverride === null || selectedInterval === intervalOverride) {
        return;
      }
      setChartInterval(intervalOverride);
    };
    t10 = [intervalOverride, selectedInterval, setChartInterval];
    $[15] = intervalOverride;
    $[16] = selectedInterval;
    $[17] = setChartInterval;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  useEffect(t9, t10);
  const yAxisProperty = useChartSettingsStore(_temp4);
  const showBreakEven = useChartSettingsStore(_temp5);
  const renderMode = useChartSettingsStore(_temp6);
  const targetBars = renderMode === "candle" ? candleChartTargetBars(plotWidth ?? 0, interval) : priceChartTargetBars(plotWidth ?? 0, renderMode);
  const request = winnerChartRequest(marketPubkey, outcomeMint, originalOutcomeIndex, interval, historyAnchorMinuteMs, tradingStartedAt, yAxisProperty, renderMode, targetBars);
  const liveChartEnabled = request.kind === "live";
  const queryArgs = request.queryArgs;
  const chartHistoryCutoffMs = winnerTradeHistoryCutoffMs(request, historyAnchorMinuteMs);
  const t11 = !liveChartEnabled;
  let t12;
  if ($[20] !== t11) {
    t12 = {
      enabled: t11
    };
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const query = useGetMarketChart(queryArgs, t12);
  const envelope = query.data;
  const convert = pickChartLeafConverter(queryArgs.property ?? "price");
  const {
    candleContractViolation,
    ...chartData
  } = mapMarketChartRows(envelope?.data ?? [], renderMode === "candle" ? "ohlcv" : "line", convert, {
    ...(envelope?.leftEdgeValue === undefined ? {} : {
      leftEdgeValue: convert(envelope.leftEdgeValue)
    }),
    ...(envelope?.bucketSeconds === undefined ? {} : {
      bucketSeconds: envelope.bucketSeconds
    }),
    ...(envelope?.rangeStart === undefined ? {} : {
      requestedFrom: envelope.rangeStart
    }),
    ...(envelope?.rangeEnd === undefined ? {} : {
      requestedTo: envelope.rangeEnd
    }),
    ...(envelope?.closedBefore === undefined ? {} : {
      closedBefore: envelope.closedBefore
    })
  });
  const chart = {
    data: query.isPending ? undefined : chartData,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch
  };
  const liveTradeRows = useWinnerLiveTrades({
    marketPubkey,
    outcomeMint,
    originalOutcomeIndex,
    tradesFrom,
    liveChartEnabled,
    ...(chartHistoryCutoffMs === undefined ? {} : {
      chartHistoryCutoffMs
    })
  });
  let t13;
  if ($[22] !== marketPubkey || $[23] !== originalOutcomeIndex || $[24] !== outcomeMint || $[25] !== tradesFrom) {
    t13 = {
      marketPubkey,
      outcomeMint,
      originalOutcomeIndex,
      tradesFrom
    };
    $[22] = marketPubkey;
    $[23] = originalOutcomeIndex;
    $[24] = outcomeMint;
    $[25] = tradesFrom;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const trades = useWinnerTradeMarkers(t13);
  let t14;
  if ($[27] !== marketPubkey || $[28] !== originalOutcomeIndex || $[29] !== outcomeMint) {
    t14 = {
      marketPubkey,
      outcomeMint,
      originalOutcomeIndex
    };
    $[27] = marketPubkey;
    $[28] = originalOutcomeIndex;
    $[29] = outcomeMint;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const tradeTape = useWinnerTradeTape(t14);
  const ownedPositionScope = useOwnedMarketPositionScope();
  let t15;
  if ($[31] !== outcomeMint) {
    t15 = [outcomeMint];
    $[31] = outcomeMint;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== marketPubkey || $[34] !== ownedPositionScope.userId || $[35] !== ownedPositionScope.walletPubkeys || $[36] !== t15) {
    t16 = {
      userId: ownedPositionScope.userId,
      walletPubkeys: ownedPositionScope.walletPubkeys,
      market: marketPubkey,
      outcomeMints: t15
    };
    $[33] = marketPubkey;
    $[34] = ownedPositionScope.userId;
    $[35] = ownedPositionScope.walletPubkeys;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const positions = useMarketPositions(t16);
  const liveCandleEnabled = liveChartEnabled && renderMode === "candle";
  const liveCandleNowSec = useLiveCandleClock(renderMode === "candle", anchorMinuteMs);
  const source = winnerChartSource({
    request,
    liveTradeRows,
    liveCandleEnabled,
    liveCandleNowSec,
    chartData: chart.data,
    ...(envelope?.rangeStart === undefined ? {} : {
      rangeStart: envelope.rangeStart
    })
  });
  const targetFrame = buildWinnerTargetFrame({
    source,
    anchorMinuteMs,
    originalOutcomeIndex,
    yAxisProperty,
    indexedOutcomeMintSupplyD6,
    outcomeMint,
    renderMode
  });
  const targetReady = source.kind === "live" || anchorMinuteMs > 0 && !chart.isPending && !chart.isError && chart.data !== undefined;
  let t17;
  if ($[38] !== setIntervalOverride) {
    t17 = nextInterval => {
      setHistoryAnchorMinuteMs(roundDownToMinute(Date.now()));
      setIntervalOverride(nextInterval);
    };
    $[38] = setIntervalOverride;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== interval || $[41] !== t17 || $[42] !== targetFrame || $[43] !== targetReady) {
    t18 = {
      interval,
      targetFrame,
      targetReady,
      commitInterval: t17
    };
    $[40] = interval;
    $[41] = t17;
    $[42] = targetFrame;
    $[43] = targetReady;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  const {
    renderedFrame,
    handleIntervalChange,
    handleWindowTransitionComplete
  } = useWinnerTimeframeFrame(t18);
  useWinnerChartFailureLogs({
    chart,
    historyStage: "values",
    candleContractFingerprint: chart.isPending || chart.isError ? undefined : candleContractViolation?.fingerprint,
    liveChartEnabled,
    outcomeMint,
    interval,
    request,
    marketPubkey,
    positionsError: positions.isError
  });
  let t19;
  if ($[45] !== effectiveMarketCapD6 || $[46] !== indexedOutcomeMintSupplyD6 || $[47] !== label || $[48] !== marketPubkey || $[49] !== originalOutcomeIndex || $[50] !== outcomeMint || $[51] !== ownedPositionScope.activeWalletPubkey || $[52] !== positions.rows || $[53] !== priceD9 || $[54] !== tradesFrom || $[55] !== yAxisProperty) {
    t19 = buildWinnerOutcomePresentation({
      props: {
        marketPubkey,
        outcomeMint,
        originalOutcomeIndex,
        tradesFrom,
        label,
        priceD9,
        effectiveMarketCapD6,
        indexedOutcomeMintSupplyD6
      },
      yAxisProperty,
      positions: positions.rows,
      activeWalletPubkey: ownedPositionScope.activeWalletPubkey
    });
    $[45] = effectiveMarketCapD6;
    $[46] = indexedOutcomeMintSupplyD6;
    $[47] = label;
    $[48] = marketPubkey;
    $[49] = originalOutcomeIndex;
    $[50] = outcomeMint;
    $[51] = ownedPositionScope.activeWalletPubkey;
    $[52] = positions.rows;
    $[53] = priceD9;
    $[54] = tradesFrom;
    $[55] = yAxisProperty;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  const presentation = t19;
  const displayFrame = renderedFrame ?? targetFrame;
  const chartLoading = renderedFrame === null && !chart.isError;
  const t20 = targetFrame.seriesKey;
  const t21 = chart.isError;
  let t22;
  if ($[57] !== tError) {
    t22 = tError("description");
    $[57] = tError;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== tError) {
    t23 = tError("retry");
    $[59] = tError;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  return <WinnerChartView marketPubkey={marketPubkey} outcomeMint={outcomeMint} label={label} resolvedAtTimestamp={resolvedAtTimestamp} resolvedAtLabel={resolvedAtLabel} interval={interval} availableIntervals={availableIntervals} yAxisProperty={yAxisProperty} renderMode={renderMode} showBreakEven={showBreakEven} candleNowSeconds={liveCandleNowSec} source={source} displayFrame={displayFrame} targetSeriesKey={t20} chartLoading={chartLoading} seriesOpacity={renderedFrame !== null && !targetReady ? TIMEFRAME_TARGET_LOADING_SERIES_OPACITY : 1} chartError={t21} errorDescription={t22} retryLabel={t23} onRetry={() => void chart.refetch()} handleIntervalChange={handleIntervalChange} handleWindowTransitionComplete={handleWindowTransitionComplete} presentation={presentation} trades={trades} tradeTape={tradeTape} onPlotWidthChange={setPlotWidth} />;
}
function _temp6(state_3) {
  return state_3.renderMode;
}
function _temp5(state_2) {
  return state_2.showBreakEven;
}
function _temp4(state_1) {
  return state_1.yAxisProperty;
}
function _temp3(state_0) {
  return state_0.setInterval;
}
function _temp2(state) {
  return state.interval;
}
function WinnerChartView(t0) {
  const $ = _c(58);
  const {
    marketPubkey,
    outcomeMint,
    label,
    resolvedAtTimestamp,
    resolvedAtLabel,
    interval,
    availableIntervals,
    yAxisProperty,
    renderMode,
    showBreakEven,
    candleNowSeconds,
    source,
    displayFrame,
    targetSeriesKey,
    chartLoading,
    seriesOpacity,
    chartError,
    errorDescription,
    retryLabel,
    onRetry,
    handleIntervalChange,
    handleWindowTransitionComplete,
    presentation,
    trades,
    tradeTape,
    onPlotWidthChange
  } = t0;
  const setChartMode = useChartSettingsStore(_temp7);
  const t1 = yAxisProperty === "marketCap" ? "market_cap" : "price";
  let t2;
  if ($[0] !== presentation.outcome) {
    t2 = [presentation.outcome];
    $[0] = presentation.outcome;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== presentation.liveValues) {
    t3 = presentation.liveValues === undefined ? {} : {
      liveValues: presentation.liveValues
    };
    $[2] = presentation.liveValues;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== resolvedAtLabel || $[5] !== resolvedAtTimestamp) {
    t4 = resolvedAtTimestamp === undefined || resolvedAtLabel === undefined ? {} : {
      resolvedAtTimestamp,
      resolvedAtLabel
    };
    $[4] = resolvedAtLabel;
    $[5] = resolvedAtTimestamp;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== displayFrame.historyStartTime) {
    t5 = displayFrame.historyStartTime === undefined ? {} : {
      historyStartTime: displayFrame.historyStartTime
    };
    $[7] = displayFrame.historyStartTime;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== displayFrame.dataWindowSecs) {
    t6 = displayFrame.dataWindowSecs === undefined ? {} : {
      dataWindowSecs: displayFrame.dataWindowSecs
    };
    $[9] = displayFrame.dataWindowSecs;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = renderMode === "line" && displayFrame.historyStartTime === undefined;
  const t8 = renderMode === "line";
  let t9;
  if ($[11] !== candleNowSeconds || $[12] !== chartLoading || $[13] !== displayFrame.data.candleWidthSeconds || $[14] !== displayFrame.data.chartData || $[15] !== displayFrame.lineCurve || $[16] !== displayFrame.windowSecs || $[17] !== handleWindowTransitionComplete || $[18] !== onPlotWidthChange || $[19] !== presentation.breakevens || $[20] !== renderMode || $[21] !== seriesOpacity || $[22] !== showBreakEven || $[23] !== t2 || $[24] !== t3 || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8 || $[30] !== targetSeriesKey || $[31] !== tradeTape || $[32] !== trades || $[33] !== yAxisProperty) {
    t9 = <MarketChart outcomes={t2} chartData={displayFrame.data.chartData} loading={chartLoading} {...t3} chartModeOverride="live" yAxisPropertyOverride={yAxisProperty} mode={renderMode} focusedOutcomeIndex={0} {...t4} showBreakEven={showBreakEven} breakevens={presentation.breakevens} trades={trades} tradeTape={tradeTape} candleWidthSeconds={displayFrame.data.candleWidthSeconds} candleNowSeconds={candleNowSeconds} height={CHART_CANVAS_HEIGHT_DESKTOP} mobileHeight={CHART_CANVAS_HEIGHT_MOBILE} windowSecs={displayFrame.windowSecs} {...t5} {...t6} onWindowTransitionComplete={handleWindowTransitionComplete} onPlotWidthChange={onPlotWidthChange} snapKey={targetSeriesKey} lineCurve={displayFrame.lineCurve} seriesOpacity={seriesOpacity} seriesColorVar="var(--foreground-brand)" extendLeftEdge={t7} holdLastValueToRightEdge={t8} dotsBackground={true} className="w-full" />;
    $[11] = candleNowSeconds;
    $[12] = chartLoading;
    $[13] = displayFrame.data.candleWidthSeconds;
    $[14] = displayFrame.data.chartData;
    $[15] = displayFrame.lineCurve;
    $[16] = displayFrame.windowSecs;
    $[17] = handleWindowTransitionComplete;
    $[18] = onPlotWidthChange;
    $[19] = presentation.breakevens;
    $[20] = renderMode;
    $[21] = seriesOpacity;
    $[22] = showBreakEven;
    $[23] = t2;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = targetSeriesKey;
    $[31] = tradeTape;
    $[32] = trades;
    $[33] = yAxisProperty;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== chartError || $[36] !== errorDescription || $[37] !== onRetry || $[38] !== retryLabel || $[39] !== source.kind) {
    t10 = source.kind === "historical" && chartError ? <ChartFetchFailure description={errorDescription} retryLabel={retryLabel} onRetry={onRetry} /> : null;
    $[35] = chartError;
    $[36] = errorDescription;
    $[37] = onRetry;
    $[38] = retryLabel;
    $[39] = source.kind;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== label) {
    t11 = [{
      index: 0,
      title: label,
      color: "var(--foreground-brand)"
    }];
    $[41] = label;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ChartSettingsMenu />;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== availableIntervals || $[45] !== handleIntervalChange || $[46] !== interval || $[47] !== outcomeMint || $[48] !== setChartMode || $[49] !== t11 || $[50] !== yAxisProperty) {
    t13 = <TooltipProvider delayDuration={300}><ChartControls marketPubkey={outcomeMint} outcomes={t11} availableIntervals={availableIntervals} chartMode="live" interval={interval} yAxisProperty={yAxisProperty} onChartModeChange={setChartMode} onIntervalChange={handleIntervalChange} priceMarketCapOnly={true} chartToolbarExtras={t12} /></TooltipProvider>;
    $[44] = availableIntervals;
    $[45] = handleIntervalChange;
    $[46] = interval;
    $[47] = outcomeMint;
    $[48] = setChartMode;
    $[49] = t11;
    $[50] = yAxisProperty;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] !== marketPubkey || $[53] !== t1 || $[54] !== t10 || $[55] !== t13 || $[56] !== t9) {
    t14 = <div className="flex min-w-0 flex-col gap-4" data-trade-chart-market={marketPubkey} data-trade-chart-metric={t1}>{t9}{t10}{t13}</div>;
    $[52] = marketPubkey;
    $[53] = t1;
    $[54] = t10;
    $[55] = t13;
    $[56] = t9;
    $[57] = t14;
  } else {
    t14 = $[57];
  }
  return t14;
}
function _temp7(state) {
  return state.setChartMode;
}
interface WinnerBreakEvenArgs {
  readonly positions: ReturnType<typeof useMarketPositions>['rows'];
  readonly activeWalletPubkey: string | null;
  readonly outcomeMint: string;
  readonly originalOutcomeIndex: number;
  readonly indexedOutcomeMintSupplyD6: string;
}
function winnerBreakEven({
  positions,
  activeWalletPubkey,
  outcomeMint,
  originalOutcomeIndex,
  indexedOutcomeMintSupplyD6
}: WinnerBreakEvenArgs): ReadonlyArray<{
  outcomeIndex: number;
  priceUsdc: number;
  marketCapUsdc: number;
}> {
  const position = positions.find(row => row.wallet === activeWalletPubkey && row.outcomeMint === outcomeMint && row.outcomeIndex === originalOutcomeIndex);
  if (position === undefined || !/^\d+$/.test(position.balance)) return [];
  if (BigInt(position.balance) <= BigInt(0)) return [];
  const priceD9 = averageEntryPriceD9(position);
  if (priceD9 <= BigInt(0)) return [];
  const marketCapD6 = breakEvenMarketCapD6(priceD9, {
    graduated: true,
    indexedOutcomeMintSupplyD6
  });
  if (marketCapD6 === undefined) return [];
  return [{
    outcomeIndex: 0,
    priceUsdc: Number(priceD9) / D9_SCALE,
    marketCapUsdc: Number(marketCapD6) / D6_SCALE
  }];
}
function ChartFetchFailure(t0) {
  const $ = _c(8);
  const {
    description,
    retryLabel,
    onRetry
  } = t0;
  let t1;
  if ($[0] !== description) {
    t1 = <span>{description}</span>;
    $[0] = description;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onRetry || $[3] !== retryLabel) {
    t2 = <AnimatedButton type="button" size="xs" variant="tertiary" onClick={onRetry}>{retryLabel}</AnimatedButton>;
    $[2] = onRetry;
    $[3] = retryLabel;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div role="alert" className="flex items-center justify-between gap-3 rounded-control bg-background-secondary px-3 py-2 text-caption text-foreground-secondary">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function rangeStartSeconds(rangeStart: string | undefined): number | undefined {
  if (rangeStart === undefined) return undefined;
  const startMs = Date.parse(rangeStart);
  return Number.isFinite(startMs) ? startMs / 1_000 : undefined;
}
function useRollingMinuteAnchorMs() {
  return useSyncExternalStore(subscribeMinuteAnchor, readMinuteAnchor, readServerMinuteAnchor);
}
function roundDownToMinute(valueMs: number): number {
  return Math.floor(valueMs / 60_000) * 60_000;
}
const minuteAnchorListeners: Set<() => void> = new Set();
let currentMinuteAnchorMs = 0;
let minuteAnchorTimeout: ReturnType<typeof setTimeout> | undefined;
function subscribeMinuteAnchor(listener: () => void): () => void {
  const isFirstListener = minuteAnchorListeners.size === 0;
  minuteAnchorListeners.add(listener);
  if (isFirstListener) {
    currentMinuteAnchorMs = roundDownToMinute(Date.now());
    scheduleMinuteAnchorTick();
  }
  return () => {
    minuteAnchorListeners.delete(listener);
    if (minuteAnchorListeners.size > 0) return;
    if (minuteAnchorTimeout !== undefined) clearTimeout(minuteAnchorTimeout);
    minuteAnchorTimeout = undefined;
    currentMinuteAnchorMs = 0;
  };
}
function scheduleMinuteAnchorTick(): void {
  if (minuteAnchorListeners.size === 0) return;
  const nowMs = Date.now();
  minuteAnchorTimeout = setTimeout(() => {
    const nextMinuteAnchorMs = roundDownToMinute(Date.now());
    if (currentMinuteAnchorMs !== nextMinuteAnchorMs) {
      currentMinuteAnchorMs = nextMinuteAnchorMs;
      for (const listener of minuteAnchorListeners) listener();
    }
    if (minuteAnchorListeners.size > 0) scheduleMinuteAnchorTick();else minuteAnchorTimeout = undefined;
  }, 60_000 - nowMs % 60_000);
}
function readMinuteAnchor(): number {
  return currentMinuteAnchorMs;
}
function readServerMinuteAnchor(): number {
  return 0;
}
function fixedPointNumber(value: string, scale: number): number | undefined {
  if (!/^\d+$/.test(value)) return undefined;
  const result = Number(value) / scale;
  return Number.isFinite(result) && result >= 0 ? result : undefined;
}
function chartWindowSeconds(interval: HistoricalWinnerChartInterval, historyStartTime: number | undefined, mountedAtMs: number): number {
  if (interval !== 'all') return WINNER_WINDOW_SECONDS[interval];
  const nowSec = mountedAtMs / 1_000;
  return Math.max(1, Math.ceil(nowSec - (historyStartTime ?? nowSec)));
}
function safeErrorClass(error: Error | null): string {
  return error?.name ?? 'unknown';
}